import {
  formValuesToCustomLimits,
  getDefaultLimitValues,
  parseLimitValue,
  PlanLimitsForm,
  PlanLimitsFormValues,
} from '@/components/settings/Billing/PlanLimitsForm';
import { useDashboardContext } from '@/contexts/DashboardContext';
import { SnackbarContext } from '@/contexts/SnackbarContext';
import { UserContext } from '@/contexts/UserContext';
import { updateSubscriptionLimits } from '@/services/CustomPlanService';
import Modal, { ModalActionData } from '@superfunnel/ui-core/Modal';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function EditSubscriptionLimitsModal({ open, onClose }: Props) {
  const { show } = useContext(SnackbarContext);
  const { selectedAccount } = useContext(UserContext);
  const { subscription } = useDashboardContext();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error>();
  const [formValues, setFormValues] = useState<PlanLimitsFormValues>(
    getDefaultLimitValues(),
  );

  // Initialize from subscription/account data
  useEffect(() => {
    if (!open) return;

    const subscriptionData = subscription?.data();
    const accountData = selectedAccount?.data();

    // Prefer account.limits, fallback to subscription.customLimits
    const limits = accountData?.limits || subscriptionData?.customLimits || {};

    setFormValues({
      domains: parseLimitValue(limits.domains),
      pages: parseLimitValue(limits.pages),
      publishedPages: parseLimitValue(limits.publishedPages),
      members: parseLimitValue(limits.members),
      aiCredits: parseLimitValue(limits.aiCredits),
      buffer: String(limits.buffer ?? 0),
    });
    setSaving(false);
    setError(undefined);
  }, [open, subscription, selectedAccount]);

  const handleSave = useCallback(async () => {
    if (saving) return;
    if (!selectedAccount) return;

    setSaving(true);
    setError(undefined);

    try {
      const customLimits = formValuesToCustomLimits(formValues);

      await updateSubscriptionLimits({
        accountId: selectedAccount.id,
        customLimits,
      });

      show('Subscription limits updated successfully', 'success');
      onClose();
    } catch (err) {
      console.error('Failed to update subscription limits:', err);
      setError(
        err instanceof Error
          ? err
          : new Error('Failed to update subscription limits'),
      );
    } finally {
      setSaving(false);
    }
  }, [saving, selectedAccount, formValues, show, onClose]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        type: 'secondary',
        title: 'Cancel',
        onClick: onClose,
        disabled: saving,
      },
      {
        type: 'primary',
        title: saving ? 'Saving...' : 'Save Limits',
        onClick: handleSave,
        loading: saving,
      },
    ],
    [saving, onClose, handleSave],
  );

  const subscriptionData = subscription?.data();
  const planInfo = subscriptionData
    ? `${subscriptionData.planName || subscriptionData.planType} (${subscriptionData.lemonSqueezyVariantId})`
    : 'No subscription';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit Subscription Limits (Admin)"
      description={`Configure limits for: ${planInfo}`}
      actions={actions}
      width="2xl"
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-gray-500">
          Set custom limits for this subscription. Leave a field empty and check
          &quot;Unlimited&quot; for no limit. These limits will take effect
          immediately.
        </p>

        <PlanLimitsForm
          values={formValues}
          onChange={setFormValues}
          disabled={saving}
        />

        {error && <p className="text-sm text-red-500">{error.message}</p>}
      </div>
    </Modal>
  );
}
